import React, { useEffect, useRef, useState } from 'react';
import { TrendingUp, Wallet, BarChart3, Shield } from 'lucide-react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const PerformanceSection = () => {
  const [ref, isVisible] = useScrollAnimation({ threshold: 0.2 });
  const [countedValue, setCountedValue] = useState(0);
  const [hoveredBar, setHoveredBar] = useState(null);

  // Animate the percentage counter with easing
  useEffect(() => {
    if (!isVisible) return;
    
    const target = 5;
    const duration = 2500;
    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Ease out cubic
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const current = target * easeOut;
      
      setCountedValue(parseFloat(current.toFixed(1)));
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }, [isVisible]);

  // Generate mock chart bars for the 7-month performance
  const monthlyData = [
    { month: 'Jun', growth: 4.2, color: 'from-green-500/70 to-green-400/50' },
    { month: 'Jul', growth: 5.8, color: 'from-green-500/80 to-green-400/60' },
    { month: 'Aug', growth: 3.9, color: 'from-green-500/70 to-green-400/50' },
    { month: 'Sep', growth: 6.1, color: 'from-green-500/90 to-green-400/70' },
    { month: 'Oct', growth: 4.5, color: 'from-green-500/70 to-green-400/50' },
    { month: 'Nov', growth: 5.2, color: 'from-green-500/80 to-green-400/60' },
    { month: 'Dec', growth: 5.3, color: 'from-green-500/80 to-green-400/60' }
  ];

  const maxGrowth = Math.max(...monthlyData.map(d => d.growth));

  return (
    <section ref={ref} className="relative bg-black py-16 md:py-24 lg:py-32 overflow-hidden">
      {/* Background Crypto Image */}
      <div className="absolute inset-0">
        <img 
          src="https://images.unsplash.com/photo-1518546305927-5a555bb7020d?q=80&w=2969&auto=format&fit=crop"
          alt=""
          className="w-full h-full object-cover opacity-[0.05]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/95 to-black" />
      </div>
      
      {/* Ambient Effects */}
      <div className="absolute top-1/2 left-0 -translate-y-1/2 w-[300px] md:w-[600px] h-[300px] md:h-[600px] bg-gradient-radial from-green-900/10 via-transparent to-transparent blur-3xl" />
      
      <div className="relative z-10 max-w-[1600px] mx-auto px-4 md:px-8 lg:px-16">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Left - Performance Stats */}
          <div
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
              transition: 'all 1s cubic-bezier(0.16, 1, 0.3, 1)'
            }}
          >
            <p className="text-white/30 text-[10px] md:text-xs font-medium tracking-[0.2em] md:tracking-[0.3em] mb-4 md:mb-6">
              [ PERFORMANCE ]
            </p>
            <h2 className="text-2xl md:text-3xl lg:text-5xl font-light text-white leading-tight tracking-tight mb-4 md:mb-6">
              Consistent Growth Over Time
            </h2>
            
            {/* Main Stat with Animation */}
            <div className="mb-6 md:mb-10">
              <div className="flex items-end gap-1 md:gap-2">
                <div className="relative">
                  {/* Glow Effect */}
                  <div 
                    className="absolute inset-0 blur-2xl bg-green-500/20 rounded-full transition-opacity duration-1000"
                    style={{ opacity: isVisible ? 1 : 0 }}
                  />
                  <span className="relative text-4xl md:text-6xl lg:text-8xl font-extralight text-green-400 tabular-nums">
                    {countedValue}%
                  </span>
                </div>
                <span className="text-white/40 text-xs md:text-lg mb-2 md:mb-4">avg. monthly</span>
              </div>
              <p className="text-white/50 text-[11px] md:text-base font-light mt-1 md:mt-2">
                Average wallet growth across our clients over the past 7 months
              </p>
            </div>

            {/* Key Points with staggered animation */}
            <div className="grid grid-cols-2 gap-4 md:gap-6">
              {[
                { icon: TrendingUp, color: 'text-green-400', title: 'Steady Returns', desc: 'Consistent performance', delay: 0 },
                { icon: Shield, color: 'text-amber-500', title: 'Risk Managed', desc: 'Controlled drawdowns', delay: 100 },
                { icon: Wallet, color: 'text-blue-400', title: 'Your Custody', desc: 'Funds stay with you', delay: 200 },
                { icon: BarChart3, color: 'text-purple-400', title: 'Transparent', desc: 'Real-time tracking', delay: 300 }
              ].map((item, index) => (
                <div 
                  key={item.title}
                  className="flex items-start gap-2 md:gap-3 group"
                  style={{
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible ? 'translateX(0)' : 'translateX(-20px)',
                    transition: `all 0.6s cubic-bezier(0.16, 1, 0.3, 1) ${600 + item.delay}ms`
                  }}
                >
                  <div className="w-8 h-8 md:w-10 md:h-10 bg-white/[0.03] border border-white/10 rounded flex items-center justify-center flex-shrink-0 group-hover:bg-white/[0.06] group-hover:border-white/20 transition-all duration-300">
                    <item.icon className={`w-4 h-4 md:w-5 md:h-5 ${item.color}`} />
                  </div>
                  <div>
                    <p className="text-white text-[11px] md:text-sm font-medium">{item.title}</p>
                    <p className="text-white/40 text-[9px] md:text-xs">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right - Visual Chart */}
          <div
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateX(0)' : 'translateX(40px)',
              transition: 'all 1s cubic-bezier(0.16, 1, 0.3, 1) 300ms'
            }}
          >
            <div className="bg-white/[0.02] border border-white/[0.06] rounded-lg p-4 md:p-6 lg:p-8 backdrop-blur-sm">
              <div className="flex items-center justify-between mb-6 md:mb-8">
                <h4 className="text-white text-sm md:text-lg font-light">Past 7 Months</h4>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  <span className="text-green-400 text-[10px] md:text-sm font-medium">+5% avg</span>
                </div>
              </div>
              
              {/* Bar Chart with Animations */}
              <div className="flex items-end justify-between gap-2 md:gap-4 h-40 md:h-56">
                {monthlyData.map((data, index) => {
                  const heightPercent = (data.growth / maxGrowth) * 100;
                  const isHovered = hoveredBar === index;
                  
                  return (
                    <div 
                      key={data.month} 
                      className="flex-1 flex flex-col items-center justify-end h-full cursor-pointer"
                      onMouseEnter={() => setHoveredBar(index)}
                      onMouseLeave={() => setHoveredBar(null)}
                    >
                      {/* Value Label */}
                      <div 
                        className="mb-2 transition-all duration-300"
                        style={{
                          opacity: isHovered ? 1 : 0,
                          transform: isHovered ? 'translateY(0)' : 'translateY(10px)'
                        }}
                      >
                        <span className="text-green-400 text-[10px] md:text-xs font-medium">
                          +{data.growth}%
                        </span>
                      </div>
                      
                      {/* Bar */}
                      <div 
                        className={`relative w-full rounded-t overflow-hidden transition-all duration-500 ${
                          isHovered ? 'scale-105' : 'scale-100'
                        }`}
                        style={{
                          height: isVisible ? `${heightPercent}%` : '0%',
                          transitionDelay: `${index * 100 + 400}ms`,
                          transitionProperty: 'height, transform',
                          minHeight: isVisible ? '8px' : '0'
                        }}
                      >
                        {/* Gradient Bar */}
                        <div className={`absolute inset-0 bg-gradient-to-t ${data.color}`} />
                        
                        {/* Shine Effect */}
                        <div 
                          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                          style={{
                            transform: 'translateX(-100%)',
                            animation: isVisible ? `shine 2s ease-out ${index * 0.15 + 0.8}s forwards` : 'none'
                          }}
                        />
                        
                        {/* Glow on Hover */}
                        {isHovered && (
                          <div className="absolute inset-0 bg-green-400/20 animate-pulse" />
                        )}
                        
                        {/* Top Glow Line */}
                        <div 
                          className="absolute top-0 left-0 right-0 h-[2px] bg-green-400 shadow-lg shadow-green-500/50"
                          style={{
                            opacity: isVisible ? 1 : 0,
                            transition: 'opacity 0.5s ease',
                            transitionDelay: `${index * 100 + 800}ms`
                          }}
                        />
                      </div>
                      
                      {/* Month Label */}
                      <span 
                        className={`text-[9px] md:text-xs mt-2 md:mt-3 transition-colors duration-300 ${
                          isHovered ? 'text-green-400' : 'text-white/40'
                        }`}
                      >
                        {data.month}
                      </span>
                    </div>
                  );
                })}
              </div>
              
              {/* Animated Baseline */}
              <div 
                className="h-[1px] bg-gradient-to-r from-transparent via-green-500/30 to-transparent mt-1"
                style={{
                  transform: isVisible ? 'scaleX(1)' : 'scaleX(0)',
                  transition: 'transform 1s ease-out 1s'
                }}
              />
              
              {/* Legend */}
              <div className="mt-4 md:mt-6 pt-4 md:pt-6 border-t border-white/[0.06]">
                <div className="flex items-center justify-between text-[9px] md:text-xs">
                  <span className="text-white/40">Based on client performance</span>
                  <span className="text-white/30">2024</span>
                </div>
              </div>
            </div>
            
            {/* Disclaimer */}
            <p className="text-white/25 text-[9px] md:text-xs mt-3 md:mt-4 leading-relaxed">
              Past performance does not guarantee future results. Trading involves risk.
            </p>
          </div>
        </div>
      </div>

      {/* CSS for shine animation */}
      <style>{`
        @keyframes shine {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
      `}</style>
    </section>
  );
};

export default PerformanceSection;
